import { isBrowser } from '@phx-husky/browser-helpers';

import { ProductCardProductData } from '~/mappers/card/models';
import { ProductSearchResult } from '~/services/search/config/products/types';
import { SalsifyProductData } from '~/types/common';

import {
  EventName,
  FormSubmitEvent,
  GTMEvent,
  HeaderMenuClickEvent,
  OtherOptions,
  PageViewEvent,
  ProductItem,
  RetailerClickEvent,
  RetailerLinkConfirmation,
  RetailerLinkRejection,
  RouteChangeEvent,
  SelectItemEvent,
  SelectPromotionEvent,
  ViewElementEvent,
  ViewItemEvent,
  ViewItemListEvent,
  ViewSearchResultsEvent,
} from './model';

declare global {
  interface Window {
    dataLayer: GTMEvent[];
  }
}

const defaultEventDelayMs = 300;
const defaultMinimumViewTime = 500;
const productListName = 'products';
const promotion = 'retailer_link_click';

class GTMService {
  #eventDelayMs: number;
  #tagmanagerPluginRouteDelay = 50;
  #minimumViewTime: number;
  #currentPageEvents: GTMEvent[] = [];

  constructor(eventDelayMs: string, minimumViewTime: string) {
    this.#eventDelayMs =
      (parseInt(eventDelayMs, 10) || defaultEventDelayMs) +
      this.#tagmanagerPluginRouteDelay;
    this.#minimumViewTime =
      parseInt(minimumViewTime, 10) || defaultMinimumViewTime;
  }

  get minimumViewTime() {
    return this.#minimumViewTime;
  }

  #emitEvent(event: GTMEvent): void {
    if (!isBrowser()) return;

    this.#currentPageEvents.push(event);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
  }

  #emitEventDelayed(
    event: GTMEvent,
    customDelayCounter?: number
  ): ReturnType<typeof setTimeout> {
    return setTimeout(() => {
      this.#emitEvent(event);
    }, customDelayCounter || this.#eventDelayMs);
  }

  #convertProductsToDL(
    items: Record<string, any>[],
    withIndex: boolean,
    otherOptions?: OtherOptions
  ): ProductItem[] {
    this.#emitEvent({
      // @ts-ignore
      ecommerce: null,
    });

    return items.map((item, index) => ({
      item_id: item?.barcodeNumber,
      item_name: item?.productTitle || item?.title,
      item_brand: item?.brandName?.length ? item.brandName[0] : null,
      item_category: item?.segmentDescription,
      item_category2: item?.aromaScent || undefined,
      item_variant: item?.netContentStatement,
      item_list_name: productListName,
      ...(withIndex && { index: index + 1 }),
      ...(otherOptions && { ...otherOptions }),
    }));
  }

  emitRouteChange(): void {
    this.#currentPageEvents = [];

    const event: RouteChangeEvent = { event: EventName.RouteChange };

    this.#emitEvent(event);
  }

  emitPageView(pageTemplate: string | unknown): void {
    const event: PageViewEvent = {
      page_template: pageTemplate as string,
      promotion_name: null,
    };

    this.#emitEvent(event);
  }

  emitHeaderMenuClick(elementText: string) {
    const clickEvent: HeaderMenuClickEvent = {
      event: EventName.HeaderMenuClick,
      click_text: elementText,
    };

    this.#emitEvent(clickEvent);
  }

  emitRetailerClick(retailerName: string, retailerUrl: string) {
    const clickEvent: RetailerClickEvent = {
      event: EventName.RetailerClick,
      retailer_name: retailerName,
      link_url: retailerUrl,
    };

    this.#emitEvent(clickEvent);
  }

  emitViewSearchResults(searchTerm: string) {
    const clickEvent: ViewSearchResultsEvent = {
      event: EventName.ViewSearchResults,
      search_term: searchTerm.toLocaleLowerCase(),
    };

    this.#emitEventDelayed(clickEvent, 700);
  }

  emitFormSubmit(formName: string) {
    const submitEvent: FormSubmitEvent = {
      event: EventName.GenerateLead,
      form_name: formName,
    };

    this.#emitEvent(submitEvent);
  }

  emitViewElement(componentName: string) {
    const elementSeen = this.#currentPageEvents.some(
      (event) =>
        'event' in event &&
        event.event === EventName.ViewElement &&
        (event as ViewElementEvent).element_name === componentName
    );

    if (elementSeen) return;

    const viewElementEvent: ViewElementEvent = {
      event: EventName.ViewElement,
      element_name: componentName,
    };

    this.#emitEventDelayed(viewElementEvent);
  }

  emitViewItem(
    item: SalsifyProductData,
    pageTemplate: string,
    lastProductIndex?
  ) {
    const viewItemEvent: ViewItemEvent = {
      event: EventName.ViewItem,
      page_template: pageTemplate,
      ecommerce: {
        items: this.#convertProductsToDL([item], false, {
          index: lastProductIndex || undefined,
        }),
      },
    };

    this.#emitEventDelayed(viewItemEvent);
  }

  emitViewItemList(items: SalsifyProductData[], otherOptions?: OtherOptions) {
    const viewItemListEvent: ViewItemListEvent = {
      event: EventName.ViewItemList,
      item_list_name: productListName,
      ecommerce: {
        items: this.#convertProductsToDL(items, true, otherOptions),
      },
    };

    this.#emitEventDelayed(viewItemListEvent);
  }

  emitSelectPromotion(
    item: SalsifyProductData,
    retailerName: string,
    lastProductIndex?
  ) {
    const selectPromotionEvent: SelectPromotionEvent = {
      event: EventName.SelectPromotion,
      retailer_name: retailerName,
      promotion_name: promotion,
      ecommerce: {
        items: this.#convertProductsToDL([item], false, {
          index: lastProductIndex,
        }),
      },
    };

    this.#emitEventDelayed(selectPromotionEvent);
  }

  emitSelectItem(
    item: ProductCardProductData | ProductSearchResult,
    otherOptions?: OtherOptions
  ) {
    const selectItemEvent: SelectItemEvent = {
      event: EventName.SelectItem,
      item_list_name: productListName,
      ecommerce: {
        items: this.#convertProductsToDL([item], false, otherOptions),
      },
    };

    this.#emitEvent(selectItemEvent);
  }

  emitRetailerLinkConfirmation(retailerName, linkUrl: string) {
    const retailerLinkConfirmationEvent: RetailerLinkConfirmation = {
      event: EventName.RetailerLinkConfirmation,
      retailer_name: retailerName,
      link_url: linkUrl,
    };

    this.#emitEvent(retailerLinkConfirmationEvent);
  }

  emitRetailerLinkRejection(retailerName, linkUrl: string) {
    const retailerLinkRejectionEvent: RetailerLinkRejection = {
      event: EventName.RetailerLinkRejection,
      retailer_name: retailerName,
      link_url: linkUrl,
    };

    this.#emitEvent(retailerLinkRejectionEvent);
  }
}

export const gtmService = new GTMService(
  process.env.GATSBY_TAG_DELAY || '',
  process.env.GATSBY_VISIBLE_TIMEOUT || ''
);
